/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Avatar from '@mui/material/Avatar';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSnackbar from 'components/MDSnackbar';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import PurchaseRequestView from '../report_purchase/PurchaseRequestView';

const initFind = {
  search: '',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'ALL',
  branch: 'ALL',
  best: 'ALL',
  option: '1',
};

function RpStockMinMax() {
  const mod = 'rp_stock_minmax';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboBest, setCboBest] = useState([]);
  const [notify, setNotify] = useState(null);
  const [prId, setPrId] = useState(0);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(!notify)}
      />
    );

    setTimeout(function () {
      setNotify(null);
    }, 3000);
  };

  const view = (data) => {
    setFind(data);
    setLoading(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
      best: data.best,
      option: data.option,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
        setPrId(result.pr_id);
      }
      setLoading(false);
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('branch', find.branch);
    params.append('best', find.best);
    params.append('option', find.option);

    const url = `${urlExcel}/download?${params.toString()}`;
    window.open(url, '_self');
  };

  const add = (item) => {
    setLoading(true);

    FetchData('PUT', `/rp_purchase`, app.token, {
      barcode_id: item.barcode_id,
      quantity: 1,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const imageDisplay = (item) => {
    return <Avatar alt={item.name} src={item.image} variant='rounded' />;
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setCboBest(result.best);
        setUrlExcel(result.excel);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={2}>
            <MDInput
              fullWidth
              value={find.best}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, best: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>Rating :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBest.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.option}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, option: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>เงื่อนไข :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              <MenuItem value='1'>ถึงจุดโอนย้าย</MenuItem>
              <MenuItem value='2'>ถึงจุดสั่งซื้อ</MenuItem>
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={1}>
            <MDBox display='flex' justifyContent='end' gap={1} onClick={excel}>
              <MDButton variant='gradient' color='success'>
                <Icon>table_view</Icon>&nbsp;Excel
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_inventory'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center'>No.</TableCell>
                    <TableCell>รูป</TableCell>
                    <TableCell>Barcode</TableCell>
                    <TableCell>สินค้า</TableCell>
                    <TableCell align='center'>สาขา</TableCell>
                    <TableCell align='center'>Rating</TableCell>
                    <TableCell align='right'>Min/Max</TableCell>
                    <TableCell align='right'>สาขา</TableCell>
                    <TableCell align='right'>คลัง</TableCell>
                    <TableCell align='right'>เติม</TableCell>
                    <TableCell align='right'>ซื้อ</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center'>{row.id}</TableCell>
                      <TableCell width='40'>{imageDisplay(row)}</TableCell>
                      <TableCell>
                        <MDBox>{row.barcode}</MDBox>
                        <MDBox>{row.barcode2}</MDBox>
                      </TableCell>
                      <TableCell>
                        <MDBox>{row.code}</MDBox>
                        <MDBox className='nowrap'>{row.product_name}</MDBox>
                      </TableCell>
                      <TableCell align='center'>{row.branch_code}</TableCell>
                      <TableCell align='center'>{row.best_name}</TableCell>
                      <TableCell align='right' className='nowrap'>
                        {Func.numberDisplay(row.quantity_min)} /{' '}
                        {Func.numberDisplay(row.quantity_max)}
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='color-danger'>
                          {Func.numberDisplay(row.quantity_balance)}
                        </MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='color-primary'>
                          {Func.numberDisplay(row.quantity_stock)}
                        </MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='color-info'>
                          {Func.numberDisplay(row.quantity_move)}
                        </MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                      <TableCell align='right'>
                        <MDBox className='color-success'>
                          {Func.numberDisplay(row.quantity_buy)}
                        </MDBox>
                        <MDBox>{row.unit_name}</MDBox>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_stock`), t(`menu.rp_stock_minmax`)]} />
      {renderList()}
      {renderLoading()}
      {notify}
    </MDBox>
  );
}

export default RpStockMinMax;
